import MomentUtils from '@date-io/moment';
import { Box, Button, ButtonGroup, Typography } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import cx from 'classnames';
import moment, { Moment } from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { DateString } from '../../services/types';
import useStyles from './DateRangePicker.styles';

interface IProps {
  startDate: DateString | undefined;
  endDate: DateString | undefined;
  onChange: (startDate: Date, endDate: Date) => void;
  preset?: DateRangePickerPreset[];
  hidePreset?: boolean;
  disableStartDate?: boolean;
  disableEndDate?: boolean;
}

export enum DateRangePickerPresetUnit {
  Day = 'day',
  Month = 'month',
  Year = 'year',
}
export interface DateRangePickerPreset {
  label: string;
  unit: DateRangePickerPresetUnit;
  amount: number;
  clampOnFirstDayOfUnit?: boolean;
}

const getDateOfFirstMomentOfTheDay = (date?: Moment): Date => {
  return moment(date)
    .startOf('day')
    .toDate();
};

const getDateOfLastMomentOfTheDay = (date?: Moment): Date => {
  return moment(date)
    .endOf('day')
    .toDate();
};

const getPastDateFromToday = (amount: number, unit: DateRangePickerPresetUnit, clampOnFirstDayOfUnit = false): Moment => {
  const date = moment().startOf('day');

  if (clampOnFirstDayOfUnit) {
    date.subtract(amount - 1, unit).startOf(unit);
  } else {
    date.subtract(amount, unit);
  }

  return date;
};

const DefaultDateRangePreset: DateRangePickerPreset[] = [
  { label: 'Last 2 days', unit: DateRangePickerPresetUnit.Day, amount: 2 },
  { label: 'Last 7 days', unit: DateRangePickerPresetUnit.Day, amount: 7 },
  { label: 'Last 30 days', unit: DateRangePickerPresetUnit.Day, amount: 30 },
  { label: '6 months', unit: DateRangePickerPresetUnit.Month, amount: 6 },
];

const DateRangePicker = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const { onChange, preset = DefaultDateRangePreset, hidePreset = false, disableStartDate = false, disableEndDate = false } = props;

  const [startDate, setStartDate] = useState<Date | null>(
    props.startDate
      ? getDateOfFirstMomentOfTheDay(moment(props.startDate))
      : moment()
          .subtract(preset[0].amount, preset[0].unit)
          .toDate(),
  );

  const [endDate, setEndDate] = useState<Date | null>(getDateOfLastMomentOfTheDay(moment(props.endDate)));

  const checkAndCallOnChange = (startDate: Date, endDate: Date) => {
    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      onChange(startDate, endDate);
    }
  };

  const handleClickDurationPresetButton = (unit: DateRangePickerPresetUnit, amount: number, clampOnFirstDayOfUnit = false) => {
    const today = getDateOfLastMomentOfTheDay();
    const startDate = getPastDateFromToday(amount, unit, clampOnFirstDayOfUnit);

    setStartDate(startDate.toDate());
    setEndDate(today);

    checkAndCallOnChange(startDate.toDate(), today);
  };

  const handleChangeStartDate = (date: MaterialUiPickersDate | null) => {
    if (date) {
      const d = getDateOfFirstMomentOfTheDay(date);

      setStartDate(d);

      if (endDate) {
        if (d > endDate) {
          setEndDate(null);
        } else {
          checkAndCallOnChange(d, endDate);
        }
      }
    }
  };

  const handleChangeEndDate = (date: MaterialUiPickersDate | null) => {
    if (date) {
      const d = getDateOfLastMomentOfTheDay(date);

      setEndDate(d);

      if (startDate) {
        if (d < startDate) {
          setStartDate(null);
        } else {
          checkAndCallOnChange(startDate, d);
        }
      }
    }
  };

  const isSelectedDateRangePreset = (unit: DateRangePickerPresetUnit, amount: number, clampOnFirstDayOfUnit = false): boolean => {
    if (!startDate || !endDate) {
      return false;
    }

    if (moment().format('YYYYMMDD') !== moment(endDate).format('YYYYMMDD')) {
      return false;
    }

    const diffDaysBetweenStartDateAndEndDate = moment(endDate).diff(startDate, 'days');
    const diffDaysOfPreset = moment().diff(getPastDateFromToday(amount, unit, clampOnFirstDayOfUnit), 'days');

    return diffDaysOfPreset === diffDaysBetweenStartDateAndEndDate;
  };

  useEffect(() => {
    if (props.startDate && props.endDate) {
      setStartDate(getDateOfFirstMomentOfTheDay(moment(props.startDate)));
      setEndDate(getDateOfLastMomentOfTheDay(moment(props.endDate)));
    }
  }, [props.startDate, props.endDate]);

  return (
    <Box display="flex" flex="1" height="30px">
      {!hidePreset && (
        <Box display="flex" marginRight="20px">
          <ButtonGroup variant="outlined" color="primary" size="small">
            {preset.map(({ label, unit: type, amount, clampOnFirstDayOfUnit = false }, idx) => (
              <Button
                className={cx({
                  [classes.durationButton]: true,
                  [classes.selected]: isSelectedDateRangePreset(type, amount, clampOnFirstDayOfUnit),
                })}
                onClick={() => handleClickDurationPresetButton(type, amount, clampOnFirstDayOfUnit)}
                key={`${label}-${type}-${amount}-${idx}`}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      )}
      <Box display="flex" flexDirection="row" alignItems="center">
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="MM/DD/YYYY"
            value={startDate}
            autoOk={true}
            onChange={handleChangeStartDate}
            inputProps={{
              role: 'startDate',
            }}
            style={{ width: 135 }}
            disabled={disableStartDate}
          />
          <Typography variant="body2" className={classes.tilde}>
            ~
          </Typography>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="MM/DD/YYYY"
            value={endDate}
            autoOk={true}
            onChange={handleChangeEndDate}
            inputProps={{
              role: 'endDate',
            }}
            style={{ width: 135 }}
            disabled={disableEndDate}
          />
        </MuiPickersUtilsProvider>
      </Box>
    </Box>
  );
};

export default React.memo(DateRangePicker);
